import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, X } from 'lucide-react';
import { api } from '../auth/authStore';

interface TenantFormData {
  name: string;
  subdomain: string;
  customDomain: string;
  country?: 'canada' | 'india' | 'usa' | 'uae' | 'germany' | 'france' | 'australia';
  plan: 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  status: 'ACTIVE' | 'TRIAL' | 'SUSPENDED' | 'INACTIVE';
  contact: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    company: string;
  };
  // Tenant Admin Credentials
  admin: {
    loginId: string;
    password: string;
    firstName: string;
    lastName: string;
  };
  usage: {
    users: {
      current: number;
      limit: number;
    };
    storage: {
      current: number;
      limit: number;
    };
    apiCalls: {
      current: number;
      limit: number;
    };
    features: string[];
  };
  config: {
    timezone: string;
    locale: string;
    currency: string;
  };
  metadata: {
    notes: string;
    tags: string[];
  };
}

const initialFormData: TenantFormData = {
  name: '',
  subdomain: '',
  customDomain: '',
  country: 'canada',
  plan: 'STARTER',
  status: 'TRIAL',
  contact: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: ''
  },
  admin: {
    loginId: '',
    password: '',
    firstName: '',
    lastName: ''
  },
  usage: {
    users: {
      current: 0,
      limit: 10
    },
    storage: {
      current: 0,
      limit: 5120 // 5GB in MB
    },
    apiCalls: {
      current: 0,
      limit: 10000
    },
    features: []
  },
  config: {
    timezone: 'UTC',
    locale: 'en-US',
    currency: 'USD'
  },
  metadata: {
    notes: '',
    tags: []
  }
};

const planLimits = {
  STARTER: {
    users: { current: 0, limit: 10 },
    storage: { current: 0, limit: 5120 },
    apiCalls: { current: 0, limit: 10000 },
    features: ['basic_reporting', 'email_support']
  },
  PROFESSIONAL: {
    users: { current: 0, limit: 50 },
    storage: { current: 0, limit: 51200 },
    apiCalls: { current: 0, limit: 100000 },
    features: ['basic_reporting', 'advanced_reporting', 'email_support', 'phone_support', 'api_access']
  },
  ENTERPRISE: {
    users: { current: 0, limit: 500 },
    storage: { current: 0, limit: 512000 },
    apiCalls: { current: 0, limit: 1000000 },
    features: ['basic_reporting', 'advanced_reporting', 'email_support', 'phone_support', 'api_access', 'custom_branding', 'sso_integration', 'priority_support']
  }
};

const availableFeatures = [
  'advanced_reporting',
  'api_access',
  'custom_branding',
  'sso_integration',
  'priority_support',
  'data_export',
  'audit_logs',
  'custom_fields'
];

export const TenantForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  
  const [formData, setFormData] = useState<TenantFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [subdomainChecking, setSubdomainChecking] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    if (isEditing && id) {
      fetchTenantData(id);
    }
  }, [isEditing, id]);

  // Check subdomain availability with debouncing
  useEffect(() => {
    if (!isEditing && formData.subdomain && formData.subdomain.length >= 3) {
      const timeoutId = setTimeout(() => {
        checkSubdomainAvailability(formData.subdomain);
      }, 500); // 500ms debounce

      return () => clearTimeout(timeoutId);
    } else {
      setSubdomainAvailable(null);
      setSubdomainChecking(false);
    }
  }, [formData.subdomain, isEditing]);

  useEffect(() => {
    // Update limits and features when plan changes
    setFormData(prev => ({
      ...prev,
      usage: {
        ...prev.usage,
        users: { ...prev.usage.users, limit: planLimits[prev.plan].users.limit },
        storage: { ...prev.usage.storage, limit: planLimits[prev.plan].storage.limit },
        apiCalls: { ...prev.usage.apiCalls, limit: planLimits[prev.plan].apiCalls.limit },
        features: planLimits[prev.plan].features
      }
    }));
  }, [formData.plan]);

  const checkSubdomainAvailability = async (subdomain: string) => {
    if (!subdomain || subdomain.length < 3) return;

    try {
      setSubdomainChecking(true);
      // We'll check by trying to get all tenants and see if subdomain exists
      const response = await api.get('/platform/tenants');
      const tenants = response.data.data.tenants || response.data.data;
      const tenantList = Array.isArray(tenants) ? tenants : [tenants];

      const subdomainExists = tenantList.some((tenant: any) =>
        tenant.subdomain.toLowerCase() === subdomain.toLowerCase()
      );

      setSubdomainAvailable(!subdomainExists);
    } catch (error) {
      console.error('Error checking subdomain availability:', error);
      setSubdomainAvailable(null);
    } finally {
      setSubdomainChecking(false);
    }
  };

  const fetchTenantData = async (tenantId: string) => {
    try {
      setLoading(true);
      const response = await api.get(`/platform/tenants/${tenantId}`);

      if (response.data.success) {
        const data = response.data.data;
        setFormData({
          name: data.name,
          subdomain: data.subdomain,
          customDomain: data.customDomain || '',
          plan: data.plan,
          status: data.status,
          contact: data.contact || {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            company: ''
          },
          admin: {
            loginId: '',
            password: '',
            firstName: '',
            lastName: ''
          },
          usage: {
            users: data.usage?.users || { current: 0, limit: 10 },
            storage: data.usage?.storage || { current: 0, limit: 5120 },
            apiCalls: data.usage?.apiCalls || { current: 0, limit: 10000 },
            features: data.usage?.features || []
          },
          config: {
            timezone: data.config?.timezone || 'UTC',
            locale: data.config?.locale || 'en-US',
            currency: data.config?.currency || 'USD'
          },
          metadata: data.metadata || {
            notes: '',
            tags: []
          }
        });
      } else {
        setErrors({ submit: response.data.message || 'Failed to load tenant data' });
      }
    } catch (error: any) {
      console.error('Failed to fetch tenant data:', error);
      const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to load tenant data';
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Tenant name is required';
    }

    if (!formData.subdomain.trim()) {
      newErrors.subdomain = 'Subdomain is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.subdomain)) {
      newErrors.subdomain = 'Subdomain can only contain lowercase letters, numbers, and hyphens';
    } else if (!isEditing && subdomainAvailable === false) {
      newErrors.subdomain = 'This subdomain is already taken. Please choose a different one.';
    }

    if (!formData.contact.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.contact.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.contact.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.contact.company.trim()) {
      newErrors.company = 'Company name is required';
    }

    // Validate admin credentials (only for new tenants)
    if (!isEditing) {
      if (!formData.admin.loginId.trim()) {
        newErrors.adminLoginId = 'Admin login ID is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin.loginId)) {
        newErrors.adminLoginId = 'Please enter a valid email address';
      }

      if (!formData.admin.password.trim()) {
        newErrors.adminPassword = 'Admin password is required';
      } else if (formData.admin.password.length < 8) {
        newErrors.adminPassword = 'Password must be at least 8 characters long';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);

      // Transform form data to match backend expectations
      const backendPayload: any = {
        name: formData.name,
        country: formData.country,
        plan: formData.plan,
        status: formData.status,
        config: {
          timezone: formData.config.timezone,
          locale: formData.config.locale,
          currency: formData.config.currency
        },
        metadata: {
          ...formData.metadata,
          // Store contact info in metadata since backend contact structure is different
          contactInfo: {
            firstName: formData.contact.firstName,
            lastName: formData.contact.lastName,
            email: formData.contact.email,
            phone: formData.contact.phone,
            company: formData.contact.company
          }
        }
      };

      // Only include customDomain if it has a value
      if (formData.customDomain && formData.customDomain.trim()) {
        backendPayload.customDomain = formData.customDomain.trim();
      }

      let response;
      if (isEditing) {
        response = await api.put(`/platform/tenants/${id}`, backendPayload);
      } else {
        // For creation, use the same transformed payload structure
        const createPayload = {
          name: formData.name,
          subdomain: formData.subdomain,
          plan: formData.plan,
          status: formData.status,
          contact: {
            firstName: formData.contact.firstName,
            lastName: formData.contact.lastName,
            email: formData.contact.email,
            phone: formData.contact.phone,
            company: formData.contact.company
          },
          admin: {
            loginId: formData.admin.loginId,
            password: formData.admin.password,
            firstName: formData.admin.firstName || formData.contact.firstName,
            lastName: formData.admin.lastName || formData.contact.lastName
          },
          config: {
            timezone: formData.config.timezone,
            locale: formData.config.locale,
            currency: formData.config.currency
          },
          metadata: {
            ...formData.metadata
          }
        };

        // Only include customDomain if it has a value
        if (formData.customDomain && formData.customDomain.trim()) {
          (createPayload as any).customDomain = formData.customDomain.trim();
        }

        response = await api.post('/platform/tenants', createPayload);
      }

      if (response.data.success) {
        navigate('/platform/tenants');
      } else {
        setErrors({ submit: response.data.message || 'Failed to save tenant' });
      }
    } catch (error: any) {
      console.error('Failed to save tenant:', error);
      let errorMessage = 'Failed to save tenant';

      if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;

        // Make subdomain conflict errors more user-friendly
        if (errorMessage.includes('subdomain already exists')) {
          errorMessage = `The subdomain "${formData.subdomain}" is already taken. Please choose a different subdomain.`;
        } else if (errorMessage.includes('custom domain already exists')) {
          errorMessage = `The custom domain "${formData.customDomain}" is already in use. Please choose a different domain.`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setErrors({ submit: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      const keys = field.split('.');
      if (keys.length === 1) {
        return { ...prev, [field]: value };
      } else if (keys.length === 2) {
        return {
          ...prev,
          [keys[0]]: {
            ...(prev[keys[0] as keyof TenantFormData] as any),
            [keys[1]]: value
          }
        };
      } else if (keys.length === 3) {
        return {
          ...prev,
          [keys[0]]: {
            ...(prev[keys[0] as keyof TenantFormData] as any),
            [keys[1]]: {
              ...((prev[keys[0] as keyof TenantFormData] as any)[keys[1]] as any),
              [keys[2]]: value
            }
          }
        };
      }
      return prev;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFeatureToggle = (feature: string) => {
    setFormData(prev => ({
      ...prev,
      usage: {
        ...prev.usage,
        features: prev.usage.features.includes(feature)
          ? prev.usage.features.filter(f => f !== feature)
          : [...prev.usage.features, feature]
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tenant data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/platform/tenants')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {isEditing ? 'Edit Firm' : 'Create New Firm'}
                </h1>
                <p className="text-gray-600">
                  {isEditing ? 'Update firm information and settings' : 'Add a new firm to the platform'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tenant Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter firm name"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subdomain *
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={formData.subdomain}
                    onChange={(e) => handleInputChange('subdomain', e.target.value.toLowerCase())}
                    className={`flex-1 px-3 py-2 border rounded-l-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.subdomain ? 'border-red-300' :
                      subdomainAvailable === false ? 'border-red-300' :
                      subdomainAvailable === true ? 'border-green-300' : 'border-gray-300'
                    }`}
                    placeholder="subdomain"
                    disabled={isEditing}
                  />
                  <span className="px-3 py-2 bg-gray-50 border border-l-0 border-gray-300 rounded-r-lg text-gray-500">
                    .erpca.ca
                  </span>
                </div>
                {errors.subdomain && <p className="mt-1 text-sm text-red-600">{errors.subdomain}</p>}
                {!isEditing && formData.subdomain && formData.subdomain.length >= 3 && (
                  <div className="mt-1">
                    {subdomainChecking ? (
                      <p className="text-sm text-gray-500">Checking availability...</p>
                    ) : subdomainAvailable === true ? (
                      <p className="text-sm text-green-600">✓ Subdomain is available</p>
                    ) : subdomainAvailable === false ? (
                      <p className="text-sm text-red-600">✗ Subdomain is already taken</p>
                    ) : null}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Domain
                </label>
                <input
                  type="text"
                  value={formData.customDomain}
                  onChange={(e) => handleInputChange('customDomain', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="custom.domain.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country *
                </label>
                <select
                  value={formData.country || 'canada'}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="canada">Canada</option>
                  <option value="usa">United States</option>
                  <option value="india">India</option>
                  <option value="uae">United Arab Emirates</option>
                  <option value="germany">Germany</option>
                  <option value="france">France</option>
                  <option value="australia">Australia</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">Workflows and regulations will be set based on country</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plan *
                </label>
                <select
                  value={formData.plan}
                  onChange={(e) => handleInputChange('plan', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="STARTER">Starter</option>
                  <option value="PROFESSIONAL">Professional</option>
                  <option value="ENTERPRISE">Enterprise</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status *
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="TRIAL">Trial</option>
                  <option value="ACTIVE">Active</option>
                  <option value="SUSPENDED">Suspended</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Contact Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.contact.firstName}
                  onChange={(e) => handleInputChange('contact.firstName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.firstName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="First name"
                />
                {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.contact.lastName}
                  onChange={(e) => handleInputChange('contact.lastName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.lastName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Last name"
                />
                {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.contact.email}
                  onChange={(e) => handleInputChange('contact.email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="email@example.com"
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.contact.phone}
                  onChange={(e) => handleInputChange('contact.phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.contact.company}
                  onChange={(e) => handleInputChange('contact.company', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.company ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Company name"
                />
                {errors.company && <p className="mt-1 text-sm text-red-600">{errors.company}</p>}
              </div>
            </div>
          </div>

          {/* Tenant Admin Credentials - Only show for new tenants */}
          {!isEditing && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Tenant Admin Credentials</h3>
            <p className="text-sm text-gray-600 mb-6">
              Set up the login credentials for the tenant administrator who will manage this account.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Login ID (Email) *
                </label>
                <input
                  type="email"
                  value={formData.admin.loginId}
                  onChange={(e) => handleInputChange('admin.loginId', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.adminLoginId ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="admin@tenant.com"
                />
                {errors.adminLoginId && <p className="mt-1 text-sm text-red-600">{errors.adminLoginId}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Password *
                </label>
                <input
                  type="password"
                  value={formData.admin.password}
                  onChange={(e) => handleInputChange('admin.password', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.adminPassword ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Minimum 8 characters"
                />
                {errors.adminPassword && <p className="mt-1 text-sm text-red-600">{errors.adminPassword}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin First Name
                </label>
                <input
                  type="text"
                  value={formData.admin.firstName}
                  onChange={(e) => handleInputChange('admin.firstName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="First name (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Last Name
                </label>
                <input
                  type="text"
                  value={formData.admin.lastName}
                  onChange={(e) => handleInputChange('admin.lastName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Last name (optional)"
                />
              </div>
            </div>
          </div>
          )}

          {/* Plan Limits */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Plan Limits</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Users Limit
                </label>
                <input
                  type="number"
                  value={formData.usage.users.limit}
                  onChange={(e) => handleInputChange('usage.users.limit', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Storage Limit (MB)
                </label>
                <input
                  type="number"
                  value={formData.usage.storage.limit}
                  onChange={(e) => handleInputChange('usage.storage.limit', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Calls Limit
                </label>
                <input
                  type="number"
                  value={formData.usage.apiCalls.limit}
                  onChange={(e) => handleInputChange('usage.apiCalls.limit', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  min="1"
                />
              </div>
            </div>
          </div>

          {/* Configuration */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Configuration</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  value={formData.config.timezone}
                  onChange={(e) => handleInputChange('config.timezone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                  <option value="America/Toronto">Toronto</option>
                  <option value="America/Vancouver">Vancouver</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Locale
                </label>
                <select
                  value={formData.config.locale}
                  onChange={(e) => handleInputChange('config.locale', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="en-US">English (US)</option>
                  <option value="en-CA">English (Canada)</option>
                  <option value="fr-CA">French (Canada)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency
                </label>
                <select
                  value={formData.config.currency}
                  onChange={(e) => handleInputChange('config.currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                </select>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Features</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {availableFeatures.map((feature) => (
                <label key={feature} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.usage.features.includes(feature)}
                    onChange={() => handleFeatureToggle(feature)}
                    className="mr-3 rounded"
                  />
                  <span className="text-sm text-gray-700">
                    {feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Notes</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Internal Notes
              </label>
              <textarea
                value={formData.metadata.notes}
                onChange={(e) => handleInputChange('metadata.notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Add any internal notes about this firm..."
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/platform/tenants')}
              className="btn-secondary"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : (isEditing ? 'Update Firm' : 'Create Firm')}
            </button>
          </div>

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
